<template>
  <div class="v2-banner-cropper">
    <!-- Crop view -->
    <template v-if="croppedImg">
      <div class="v2-banner-cropper__crop-wrap">
        <cropper
          ref="bannerCropperRef"
          :src="croppedImg"
          class="v2-banner-cropper__cropper"
          :stencil-props="{ aspectRatio: 16 / 9 }"
        />
      </div>
      <div class="v2-banner-cropper__crop-actions">
        <v2-button
          variant="outline"
          @click="onCancelCrop"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :loading="isCropping"
          @click="onCrop"
        >
          Crop & Upload
        </v2-button>
      </div>
    </template>

    <!-- Upload view -->
    <template v-else>
      <!-- Upload area (only when fewer than 3 banners) -->
      <div
        v-if="hasBanner"
        class="v2-banner-cropper__upload-area"
      >
        <label class="v2-banner-cropper__upload-label">
          <input
            ref="bannerFileInput"
            type="file"
            accept="image/gif,image/jpg,image/jpeg,image/png,image/bmp,image/GIF,image/JPG,image/PNG"
            class="v2-banner-cropper__file-input"
            @change="onSelectImage"
          >
          <div class="v2-banner-cropper__upload-inner">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line
                x1="12"
                y1="3"
                x2="12"
                y2="15"
              />
            </svg>
            <span class="v2-banner-cropper__upload-text">Click to upload a banner image</span>
            <span class="v2-banner-cropper__upload-hint">Supports JPG, PNG, GIF — max 3 banners</span>
          </div>
        </label>
      </div>

      <!-- Banner thumbnails list -->
      <div
        v-if="banners && banners.length"
        class="v2-banner-cropper__list"
      >
        <div
          v-for="(banner, idx) in banners"
          :key="idx"
          class="v2-banner-cropper__item"
        >
          <img
            :src="banner"
            :alt="`Banner ${idx + 1}`"
            class="v2-banner-cropper__item-img"
          >
          <div class="v2-banner-cropper__item-info">
            <span class="v2-banner-cropper__item-label">Banner {{ idx + 1 }}</span>
          </div>
          <v2-button
            variant="outline"
            size="sm"
            @click="onRemoveBanner(idx)"
          >
            Remove
          </v2-button>
        </div>
      </div>

      <p
        v-else
        class="v2-banner-cropper__empty"
      >
        No banners uploaded yet.
      </p>

      <!-- Delete confirmation modal -->
      <v2-modal
        :show="showDeleteConfirm"
        title="Remove Banner"
        size="sm"
        @close="showDeleteConfirm = false"
      >
        <p class="v2-text-sm">
          Are you sure you want to remove this banner?
        </p>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="destructive"
            @click="onConfirmDelete"
          >
            Remove
          </v2-button>
        </template>
      </v2-modal>
    </template>

    <!-- Bottom actions -->
    <div class="v2-banner-cropper__footer">
      <v2-button
        variant="outline"
        @click="$emit('on-cancel')"
      >
        Cancel
      </v2-button>
      <v2-button
        variant="primary"
        @click="$emit('on-save')"
      >
        Done
      </v2-button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import { Cropper } from 'vue-advanced-cropper'
import { useBannerCropper } from '@/composables/useBannerCropper'
import { kpRequest, kpEndpoint } from '@/axios'
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'

export default {
  name: 'V2BannerCropper',
  components: {
    V2Button,
    V2Modal,
    Cropper,
  },
  props: {
    profileGeneral: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['on-save', 'on-cancel'],
  setup(props, { refs }) {
    const profileGeneralRef = computed(() => props.profileGeneral)
    const bc = useBannerCropper(profileGeneralRef)

    const showDeleteConfirm = ref(false)
    const pendingDeleteIdx = ref(null)

    const banners = computed(() => props.profileGeneral?.banner || [])

    async function onSelectImage(e) {
      await bc.onSelectImage(e)
    }

    function onCancelCrop() {
      bc.clearImg()
    }

    async function onCrop() {
      await bc.crop(refs.bannerCropperRef)
    }

    function onRemoveBanner(idx) {
      pendingDeleteIdx.value = idx
      showDeleteConfirm.value = true
    }

    async function onConfirmDelete() {
      const idx = pendingDeleteIdx.value
      showDeleteConfirm.value = false
      pendingDeleteIdx.value = null
      await bc.deleteBanner(idx, kpRequest, kpEndpoint)
    }

    return {
      ...bc,
      banners,
      showDeleteConfirm,
      pendingDeleteIdx,
      onSelectImage,
      onCancelCrop,
      onCrop,
      onRemoveBanner,
      onConfirmDelete,
    }
  },
}
</script>

<style lang="scss">
.v2-banner-cropper__cropper {
  .vue-advanced-cropper__foreground,
  .vue-advanced-cropper__background {
    background: var(--v2-muted);
  }

  .vue-simple-handler {
    background: var(--v2-primary);
  }

  .vue-preview--fill {
    border: 1px solid var(--v2-primary);
  }
}
</style>

<style lang="scss" scoped>
.v2-banner-cropper {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-4);

  &__crop-wrap {
    border-radius: var(--v2-radius-md);
    overflow: hidden;
    background: var(--v2-muted);
  }

  &__cropper {
    height: 300px;
  }

  &__crop-actions {
    display: flex;
    gap: var(--v2-space-3);
    justify-content: flex-end;
  }

  &__upload-area {
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
    }
  }

  &__upload-label {
    display: block;
    cursor: pointer;
    padding: var(--v2-space-6);
  }

  &__file-input {
    display: none;
  }

  &__upload-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--v2-space-2);
    color: var(--v2-muted-foreground);
  }

  &__upload-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__upload-hint {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-3);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-2);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
  }

  &__item-img {
    width: 120px;
    height: 60px;
    object-fit: cover;
    border-radius: var(--v2-radius-sm);
    border: 1px solid var(--v2-border);
    flex-shrink: 0;
  }

  &__item-info {
    flex: 1;
  }

  &__item-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__empty {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    text-align: center;
    padding: var(--v2-space-4);
  }

  &__footer {
    display: flex;
    gap: var(--v2-space-3);
    justify-content: flex-end;
    padding-top: var(--v2-space-3);
    border-top: 1px solid var(--v2-border);
  }

  @media (max-width: 640px) {
    &__item {
      flex-direction: column;
      align-items: flex-start;
    }

    &__item-img {
      width: 100%;
      height: auto;
    }

    &__crop-actions,
    &__footer {
      justify-content: stretch;

      .v2-btn {
        flex: 1;
      }
    }
  }
}
</style>
